import React, { useMemo } from 'react';
import type { OverseasShipment, ShipmentItem } from '../../../types';
import XCircleIcon from '../../icons/XCircleIcon';
import DownloadIcon from '../../icons/DownloadIcon';

interface VarianceReportModalProps {
  shipment: OverseasShipment;
  onClose: () => void;
}

interface VarianceItem {
  serialNumber?: string;
  description: string;
  expectedQuantity: number;
  receivedQuantity: number;
  variance: number;
  expectedCost?: number;
  expectedTotalAmount?: number;
  varianceAmount?: number;
  unit?: string;
  status: string;
  notes?: string;
}

const VarianceReportModal: React.FC<VarianceReportModalProps> = ({ shipment, onClose }) => {
  const varianceItems = useMemo(() => {
    return shipment.items?.map((item): VarianceItem => {
      const receivedQty = item.receivedQuantity || 0;
      const variance = receivedQty - item.expectedQuantity;
      const varianceAmount = item.expectedCost ? variance * item.expectedCost : undefined;

      return {
        serialNumber: item.expectedSerialNumber,
        description: item.expectedDescription || item.expectedName,
        expectedQuantity: item.expectedQuantity,
        receivedQuantity: receivedQty,
        variance: variance,
        expectedCost: item.expectedCost,
        expectedTotalAmount: item.expectedTotalAmount,
        varianceAmount: varianceAmount,
        unit: item.expectedUnit,
        status: item.receivingStatus,
        notes: item.notes,
      };
    }) || [];
  }, [shipment.items]);

  const summary = useMemo(() => {
    const totalExpected = varianceItems.reduce((sum, item) => sum + item.expectedQuantity, 0);
    const totalReceived = varianceItems.reduce((sum, item) => sum + item.receivedQuantity, 0);
    const totalVariance = totalReceived - totalExpected;
    const totalExpectedAmount = varianceItems.reduce((sum, item) => sum + (item.expectedTotalAmount || 0), 0);
    const totalVarianceAmount = varianceItems.reduce((sum, item) => sum + (item.varianceAmount || 0), 0);
    const itemsWithVariance = varianceItems.filter(item => item.variance !== 0).length;
    const missingItems = varianceItems.filter(item => item.status === 'missing').length;
    const damagedItems = varianceItems.filter(item => item.status === 'damaged').length;

    return {
      totalExpected,
      totalReceived,
      totalVariance,
      totalExpectedAmount,
      totalVarianceAmount,
      itemsWithVariance,
      missingItems,
      damagedItems,
    };
  }, [varianceItems]);

  const handleExportCSV = () => {
    const headers = [
      'Serial Number',
      'Description',
      'Expected Quantity',
      'Received Quantity',
      'Variance',
      'Rate/Unit',
      'Expected Total Amount',
      'Variance Amount',
      'Unit',
      'Status',
      'Notes',
    ];

    const rows = varianceItems.map(item => [
      item.serialNumber || '',
      item.description,
      item.expectedQuantity.toString(),
      item.receivedQuantity.toString(),
      item.variance.toString(),
      item.expectedCost ? item.expectedCost.toFixed(2) : '',
      item.expectedTotalAmount ? item.expectedTotalAmount.toFixed(2) : '',
      item.varianceAmount ? item.varianceAmount.toFixed(2) : '',
      item.unit || '',
      item.status,
      item.notes || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `variance_report_${shipment.shipmentNumber}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Variance Report</h2>
            <p className="text-sm text-gray-600 mt-1">
              Shipment: {shipment.shipmentNumber} - {shipment.supplierName || 'Unknown Supplier'}
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center"
            >
              <DownloadIcon className="w-5 h-5 mr-2" />
              Export CSV
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="font-semibold text-gray-800 mb-3">Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-gray-600">Total Expected</div>
              <div className="text-lg font-bold text-gray-900">{summary.totalExpected}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Received</div>
              <div className="text-lg font-bold text-gray-900">{summary.totalReceived}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Variance</div>
              <div className={`text-lg font-bold ${summary.totalVariance > 0 ? 'text-green-600' : summary.totalVariance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {summary.totalVariance > 0 ? '+' : ''}{summary.totalVariance}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Variance Amount</div>
              <div className={`text-lg font-bold ${summary.totalVarianceAmount > 0 ? 'text-green-600' : summary.totalVarianceAmount < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                ${summary.totalVarianceAmount > 0 ? '+' : ''}{summary.totalVarianceAmount.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Items with Variance</div>
              <div className="text-lg font-bold text-yellow-600">{summary.itemsWithVariance}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Missing Items</div>
              <div className="text-lg font-bold text-red-600">{summary.missingItems}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Damaged Items</div>
              <div className="text-lg font-bold text-orange-600">{summary.damagedItems}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Expected Total Amount</div>
              <div className="text-lg font-bold text-gray-900">${summary.totalExpectedAmount.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Variance Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serial #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Expected Qty</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Received Qty</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Variance</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate/Unit</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Expected Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Variance Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {varianceItems.map((item, index) => (
                <tr
                  key={index}
                  className={
                    item.status === 'missing'
                      ? 'bg-red-50'
                      : item.variance < 0
                      ? 'bg-yellow-50'
                      : item.variance > 0
                      ? 'bg-green-50'
                      : ''
                  }
                >
                  <td className="px-4 py-3 text-sm text-gray-900">{item.serialNumber || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{item.description}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">
                    {item.expectedQuantity} {item.unit || 'units'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">
                    {item.receivedQuantity} {item.unit || 'units'}
                  </td>
                  <td className={`px-4 py-3 text-sm font-semibold text-right ${
                    item.variance > 0 ? 'text-green-600' : item.variance < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {item.variance > 0 ? '+' : ''}{item.variance} {item.unit || 'units'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">
                    {item.expectedCost ? `$${item.expectedCost.toFixed(2)}` : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">
                    {item.expectedTotalAmount ? `$${item.expectedTotalAmount.toFixed(2)}` : 'N/A'}
                  </td>
                  <td className={`px-4 py-3 text-sm font-semibold text-right ${
                    item.varianceAmount && item.varianceAmount > 0 ? 'text-green-600' : item.varianceAmount && item.varianceAmount < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {item.varianceAmount !== undefined
                      ? `${item.varianceAmount > 0 ? '+' : ''}$${item.varianceAmount.toFixed(2)}`
                      : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      item.status === 'missing' ? 'bg-red-100 text-red-800' :
                      item.status === 'damaged' ? 'bg-orange-100 text-orange-800' :
                      item.status === 'received' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VarianceReportModal;
